import React, {useEffect, useRef, useState} from 'react';
import './ChatBotStyles.css';
import ReactMarkdown from 'react-markdown';
import rehypeHighlight from 'rehype-highlight';
import remarkGfm from 'remark-gfm';
import {useDispatch, useSelector} from 'react-redux';
import {addToCart} from "../../features/cart/cartSlice";
import ChatBotProductRecommendations from "./ChatBotProductRecommendations";

const ChatBot = () => {
    const {cabinets} = useSelector(state => state.showroom)
    const user = useSelector(state => state.user.user) || {};
    const [messages, setMessages] = useState([]);
    const [inputValue, setInputValue] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [serverError, setServerError] = useState(null);
    const [isChatOpen, setIsChatOpen] = useState(false);
    const [showDisclaimer, setShowDisclaimer] = useState(true);
    const [conversationId, setConversationId] = useState('');
    const [attachments, setAttachments] = useState([]);
    const [isExpanded, setIsExpanded] = useState(false);
    const [clientIp, setClientIp] = useState('');
    const [usageLimitExceeded, setUsageLimitExceeded] = useState(false);
    const [usageRestartTime, setUsageRestartTime] = useState(null);
    const messagesEndRef = useRef(null);
    const fileInputRef = useRef(null);
    const dispatch = useDispatch();
    const API_URL = process.env.REACT_APP_BASEURL + '/api/claude';

    // Check if user is logged in
    const isUserLoggedIn = Boolean(user && user.email && Object.keys(user).length > 0);
    const userEmail = isUserLoggedIn ? user.email : 'guest';

    // Auto-scroll to bottom of messages
    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({behavior: 'smooth'});
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    // Retrieve the client's public IP using ipify
    useEffect(() => {
        const fetchClientIp = async () => {
            try {
                const res = await fetch('https://api.ipify.org?format=json');
                const data = await res.json();
                setClientIp(data.ip);
            } catch (error) {
                console.error('Failed to retrieve client IP:', error);
            }
        };
        fetchClientIp();
    }, []);

    // Check if backend server is running on component mount.
    useEffect(() => {
        const checkServerStatus = async () => {
            try {
                const response = await fetch(`${API_URL}/test`, {
                    headers: {
                        'X-Client-IP': clientIp,
                    },
                });
                if (!response.ok) {
                    setServerError('Backend server is not responding correctly');
                } else {
                    setServerError(null);
                }
            } catch (error) {
                console.error('Server check failed:', error);
                setServerError('Cannot connect to backend server. Please make sure it is running.');
            }
        };

        checkServerStatus();
    }, [API_URL, clientIp]);

    // Handle file attachment selection
    const handleFileSelect = (e) => {
        const files = Array.from(e.target.files);
        const validFiles = files.filter(file => {
            const isValidType = ['image/jpeg', 'image/png', 'image/gif', 'application/pdf'].includes(file.type);
            const isValidSize = file.size <= 5 * 1024 * 1024; // 5MB max
            return isValidType && isValidSize;
        });

        if (validFiles.length !== files.length) {
            alert('Some files were rejected. Please only upload images (JPG, PNG, GIF) or PDFs under 5MB.');
        }

        if (validFiles.length > 0) {
            setAttachments(prev => [...prev, ...validFiles]);
        }
    };

    // Remove an attachment
    const removeAttachment = (index) => {
        setAttachments(prev => prev.filter((_, i) => i !== index));
    };

    // Trigger file input click
    const openFileSelector = () => {
        fileInputRef.current.click();
    };

    // Handle adding a product to cart
    const handleAddToCart = (product) => {
        const selectedCabinet = cabinets.find(
            (cabinet) => cabinet.serialNumber === product.serialNumber
        );

        // Check if the cabinet exists.
        if (!selectedCabinet) {
            console.warn(`Cabinet with serial number ${product.serialNumber} not found.`);
            return;
        }

        // Create a new object with updated properties.
        const cabinetToAdd = {
            ...selectedCabinet,
            width: product.width,
            height: product.height,
            depth: product.depth,
            quantity: 1,
            id: Date.now().toString()
        };

        dispatch(addToCart({
            // item: null,
            ...cabinetToAdd
        }));

        setMessages(prev => [
            ...prev,
            {
                text: `Added **${product.serialNumber}** to your cart.`,
                sender: 'bot',
                timestamp: new Date().toISOString(),
                isConfirmation: true
            }
        ]);

        setTimeout(() => {
            scrollToBottom();
        }, 100);
    };

    // Format the restart time in a user-friendly way
    const formatRestartTime = (isoString) => {
        const date = new Date(isoString);
        return date.toLocaleString('en-US', {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: 'numeric',
            hour12: true
        });
    };

    // Send message to the backend
    const sendMessage = async () => {
        if (!inputValue.trim() && attachments.length === 0) return;

        // If user has exceeded their usage limit, show a message instead of sending to backend
        if (usageLimitExceeded) {
            const restartTimeFormatted = usageRestartTime ? formatRestartTime(usageRestartTime) : 'soon';
            setMessages((prev) => [
                ...prev,
                {
                    text: inputValue,
                    sender: 'user',
                    timestamp: new Date().toISOString(),
                    attachments: attachments.map(file => ({
                        name: file.name,
                        type: file.type,
                        size: file.size
                    }))
                },
                {
                    text: `Sorry, you've reached your token usage limit. Your access will be restored on ${restartTimeFormatted}. If you need immediate assistance, please contact our customer support.`,
                    sender: 'bot',
                    timestamp: new Date().toISOString(),
                    isError: true
                }
            ]);
            setInputValue('');
            setAttachments([]);
            return;
        }

        const formData = new FormData();

        const userMessage = {
            text: inputValue,
            sender: 'user',
            timestamp: new Date().toISOString(),
            attachments: attachments.map(file => ({
                name: file.name,
                type: file.type,
                size: file.size
            }))
        };
        setMessages((prev) => [...prev, userMessage]);

        const messageToSend = inputValue;
        setInputValue('');
        setIsLoading(true);

        formData.append('prompt', messageToSend);
        if (conversationId) {
            formData.append('conversationId', conversationId);
        }
        attachments.forEach((file) => {
            formData.append('attachments', file);
        });
        formData.append('email', userEmail);
        setAttachments([]);

        try {
            const controller = new AbortController();
            const timeoutId = setTimeout(() => controller.abort(), 6000000); // 60 second timeout
            const response = await fetch(`${API_URL}/upload`, {
                method: 'POST',
                body: formData,
                headers: {
                    // Pass the client's IP address in a custom header
                    'X-Client-IP': clientIp,
                    'X-User-Email': userEmail,
                },
                signal: controller.signal
            });
            clearTimeout(timeoutId);

            if (!response.ok) {
                console.error(`Server responded with status: ${response.status}`);

                // Handle token limit exceeded error (429 Too Many Requests)
                if (response.status === 429) {
                    const errorData = await response.json();
                    setUsageLimitExceeded(true);
                    setUsageRestartTime(errorData.usageRestart);

                    setMessages((prev) => [
                        ...prev,
                        {
                            text: `Sorry, you've reached your token usage limit. Your access will be restored on ${formatRestartTime(errorData.usageRestart)}. If you need immediate assistance, please contact our customer support.`,
                            sender: 'bot',
                            timestamp: new Date().toISOString(),
                            isError: true
                        }
                    ]);
                    return;
                }

                throw new Error(`Server responded with status: ${response.status}`);
            }

            const data = await response.json();

            if (data.conversationId) {
                setConversationId(data.conversationId);
            }

            const botMessage = {
                text: data.text || "No response text received",
                sender: 'bot',
                timestamp: new Date().toISOString(),
                modelUsed: data.modelUsed || "Claude",
                products: data.products || []
            };

            setMessages((prev) => [...prev, botMessage]);
        } catch (error) {
            if (!usageLimitExceeded) {
                console.error('Error sending message:', error);
                setMessages((prev) => [
                    ...prev,
                    {
                        text: `Sorry, I encountered an error: ${error.message}. Please try again.`,
                        sender: 'bot',
                        timestamp: new Date().toISOString(),
                        isError: true,
                    },
                ]);
            }
        } finally {
            setIsLoading(false);
        }
    };

    // Handle form submission
    const handleSubmit = (e) => {
        if (e) e.preventDefault();
        sendMessage();
    };

    // Handle enter key press
    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendMessage();
        }
    };

    // Toggle chat window
    const toggleChat = () => {
        setIsChatOpen(!isChatOpen);
    };

    // Toggle expanded view
    const toggleExpand = () => {
        setIsExpanded(!isExpanded);
    };

    // Clear chat history
    const clearChat = () => {
        setMessages([]);
        setUsageLimitExceeded(false);
        setUsageRestartTime(null);
        if (conversationId) {
            fetch(`${API_URL}/${conversationId}`, {
                method: 'DELETE',
                headers: {
                    'X-Client-IP': clientIp,
                    'X-User-Email': userEmail, // Add user email header
                },
            }).then(response => {
                if (!response.ok) {
                    console.error('Failed to delete conversation on server');
                }
            }).catch(error => {
                console.error('Error deleting conversation:', error);
            });
        }
        setConversationId('');
        setAttachments([]);
    };

    // Hide disclaimer
    const hideDisclaimer = () => {
        setShowDisclaimer(false);
    };

    // Render attachment previews
    const renderAttachmentPreviews = () => {
        return attachments.map((file, index) => (
            <div key={index} className="sealab-livechat__attachment-preview">
                {file.type.startsWith('image/') ? (
                    <img
                        src={URL.createObjectURL(file)}
                        alt={file.name}
                        className="sealab-livechat__attachment-thumbnail"
                    />
                ) : (
                    <div className="sealab-livechat__pdf-icon">PDF</div>
                )}
                <span className="sealab-livechat__attachment-name">{file.name}</span>
                <button
                    className="sealab-livechat__attachment-remove"
                    onClick={() => removeAttachment(index)}
                    aria-label="Remove attachment"
                >
                    ×
                </button>
            </div>
        ));
    };

    return (
        <div className="sealab-livechat__wrapper">
            <button
                className={`sealab-livechat__chat-bubble ${isChatOpen ? 'sealab-livechat__chat-bubble-hidden' : ''}`}
                onClick={toggleChat}
                aria-label="Open chat"
            >
                <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" fill="currentColor" viewBox="0 0 16 16"
                     className="sealab-livechat__chat-icon">
                    <path
                        d="M0 2a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H4.414a1 1 0 0 0-.707.293L.854 15.146A.5.5 0 0 1 0 14.793V2zm3.5 1a.5.5 0 0 0 0 1h9a.5.5 0 0 0 0-1h-9zm0 2.5a.5.5 0 0 0 0 1h9a.5.5 0 0 0 0-1h-9zm0 2.5a.5.5 0 0 0 0 1h5a.5.5 0 0 0 0-1h-5z"/>
                </svg>
            </button>

            <div
                className={`sealab-livechat__container ${isChatOpen ? 'sealab-livechat__open' : ''} ${isExpanded ? 'sealab-livechat__expanded' : ''}`}>
                <div className="sealab-livechat__header">
                    <div className="sealab-livechat__title-wrapper">
                        <h5 className="sealab-livechat__title">The Sealab Assistant</h5>
                    </div>
                    <div className="sealab-livechat__controls">
                        <button
                            className="sealab-livechat__control-btn"
                            onClick={clearChat}
                            aria-label="Clear chat"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor"
                                 viewBox="0 0 16 16" className="sealab-livechat__control-icon">
                                <path
                                    d="M5.5 5.5A.5.5 0 0 1 6 6v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm2.5 0a.5.5 0 0 1 .5.5v6a.5.5 0 0 1-1 0V6a.5.5 0 0 1 .5-.5zm3 .5a.5.5 0 0 0-1 0v6a.5.5 0 0 0 1 0V6z"/>
                                <path fillRule="evenodd"
                                      d="M14.5 3a1 1 0 0 1-1 1H13v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V4h-.5a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1H6a1 1 0 0 1 1-1h2a1 1 0 0 1 1 1h3.5a1 1 0 0 1 1 1v1zM4.118 4 4 4.059V13a1 1 0 0 0 1 1h6a1 1 0 0 0 1-1V4.059L11.882 4H4.118zM2.5 3V2h11v1h-11z"/>
                            </svg>
                        </button>
                        <button
                            className="sealab-livechat__control-btn"
                            onClick={toggleExpand}
                            aria-label={isExpanded ? "Minimize chat" : "Expand chat"}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor"
                                 viewBox="0 0 16 16" className="sealab-livechat__control-icon">
                                {isExpanded ? (
                                    <path
                                        d="M.172 15.828a.5.5 0 0 0 .707 0l4.096-4.096V14.5a.5.5 0 1 0 1 0v-3.975a.5.5 0 0 0-.5-.5H1.5a.5.5 0 0 0 0 1h2.768L.172 15.121a.5.5 0 0 0 0 .707zM15.828.172a.5.5 0 0 0-.707 0l-4.096 4.096V1.5a.5.5 0 1 0-1 0v3.975a.5.5 0 0 0 .5.5H14.5a.5.5 0 0 0 0-1h-2.768L15.828.879a.5.5 0 0 0 0-.707z"/>
                                ) : (
                                    <path
                                        d="M1.828 15.828a.5.5 0 0 1-.707 0l-1.1-1.1A.5.5 0 0 1 0 14.414V1.586A.5.5 0 0 1 .5 1h12.828a.5.5 0 0 1 .354.854l-1.1 1.1a.5.5 0 0 1-.707 0L11 2.086V11.5a.5.5 0 0 1-.5.5h-9.414l.757.757a.5.5 0 0 1 0 .707l-1.1 1.1a.5.5 0 0 1-.708 0L0 14.414a.5.5 0 0 1 .146-.353l.682-.682zM16 1.414V14.5a.5.5 0 0 1-.5.5h-12.829a.5.5 0 0 1-.353-.854l1.1-1.1a.5.5 0 0 1 .707 0L5 13.914V4.5a.5.5 0 0 1 .5-.5h9.414l-.757-.757a.5.5 0 0 1 0-.707l1.1-1.1a.5.5 0 0 1 .707 0L16 1.414z"/>
                                )}
                            </svg>
                        </button>
                        <button
                            className="sealab-livechat__control-btn"
                            onClick={toggleChat}
                            aria-label="Close chat"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor"
                                 viewBox="0 0 16 16" className="sealab-livechat__control-icon">
                                <path
                                    d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z"/>
                            </svg>
                        </button>
                    </div>
                </div>

                <div className="sealab-livechat__messages">
                    {showDisclaimer && (
                        <div className="sealab-livechat__disclaimer">
                            <p>
                                This AI powered interactive assistant can make mistakes and is intended to provide
                                general guidance for The Sealab. Please verify all specifications and compatibility
                                before ordering, and contact our support team for detailed technical questions.
                            </p>
                            <p>
                                <br/>By using our assistant, you agree to our <a href="/the-sealab/legal/terms">Terms of
                                Service</a>.
                            </p>

                            <button
                                className="sealab-livechat__disclaimer-close"
                                onClick={hideDisclaimer}
                                aria-label="Close disclaimer"
                            >
                                <i className="bi bi-x"></i>
                            </button>
                        </div>
                    )}

                    {serverError && (
                        <div className="sealab-livechat__error-banner">
                            <p>{serverError}</p>
                            <p>Please check if the backend server is running and accessible.</p>
                        </div>
                    )}

                    {messages.length === 0 ? (
                        <div className="sealab-livechat__welcome">
                            <div className="sealab-livechat__welcome-text">
                                <h6>Welcome to The Sealab!</h6>
                                <p>How can I help you today?</p>
                            </div>
                        </div>
                    ) : (
                        messages.map((message, index) => (
                            <div
                                key={index}
                                className={`sealab-livechat__message ${
                                    message.sender === 'user'
                                        ? 'sealab-livechat__user-message'
                                        : 'sealab-livechat__bot-message'
                                } ${message.isError ? 'sealab-livechat__error' : ''} ${message.isConfirmation ? 'sealab-livechat__confirmation' : ''}`}
                            >
                                <div className="sealab-livechat__message-content">
                                    {message.sender === 'user' && (
                                        <div className="sealab-livechat__user-label">You</div>
                                    )}
                                    {message.sender === 'bot' && (
                                        <div className="sealab-livechat__bot-label">Assistant</div>
                                    )}
                                    <div className="sealab-livechat__markdown-content">
                                        {message.sender === 'user' ? (
                                            message.text
                                        ) : (
                                            <ReactMarkdown
                                                remarkPlugins={[remarkGfm]}
                                                rehypePlugins={[rehypeHighlight]}
                                                components={{
                                                    h1: ({node, ...props}) => <h1
                                                        className="sealab-livechat__heading" {...props} />,
                                                    h2: ({node, ...props}) => <h2
                                                        className="sealab-livechat__heading" {...props} />,
                                                    h3: ({node, ...props}) => <h3
                                                        className="sealab-livechat__heading" {...props} />,
                                                    strong: ({node, ...props}) => <strong
                                                        className="sealab-livechat__strong" {...props} />
                                                }}
                                            >
                                                {message.text}
                                            </ReactMarkdown>
                                        )}
                                    </div>
                                    {message.attachments && message.attachments.length > 0 && (
                                        <div className="sealab-livechat__message-attachments">
                                            {message.attachments.map((attachment, idx) => (
                                                <div key={idx} className="sealab-livechat__message-attachment">
                                                    {attachment.type.startsWith('image/') ? (
                                                        <div
                                                            className="sealab-livechat__message-image">{attachment.name}</div>
                                                    ) : (
                                                        <div
                                                            className="sealab-livechat__message-file">{attachment.name}</div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    {message.products && message.products.length > 0 && (
                                        <ChatBotProductRecommendations
                                            products={message.products}
                                            onAddToCart={handleAddToCart}
                                        />
                                    )}
                                    <small className="sealab-livechat__timestamp">
                                        {new Date(message.timestamp).toLocaleTimeString([], {
                                            hour: '2-digit',
                                            minute: '2-digit',
                                        })}
                                    </small>
                                </div>
                            </div>
                        ))
                    )}
                    {isLoading && (
                        <div className="sealab-livechat__message sealab-livechat__bot-message">
                            <div className="sealab-livechat__message-content">
                                <div className="sealab-livechat__message-header">
                                    <div className="sealab-livechat__bot-label">Sealab Assistant</div>
                                </div>
                                <div className="sealab-livechat__typing-indicator">
                                    <span></span>
                                    <span></span>
                                    <span></span>
                                </div>
                            </div>
                        </div>
                    )}
                    <div ref={messagesEndRef}/>
                </div>

                {attachments.length > 0 && (
                    <div className="sealab-livechat__attachments-container">
                        {renderAttachmentPreviews()}
                    </div>
                )}

                <form onSubmit={handleSubmit} className="sealab-livechat__input-form">
                    <div className="sealab-livechat__input-container">
                        <textarea
                            className="form-control sealab-livechat__input"
                            value={inputValue}
                            onChange={(e) => setInputValue(e.target.value)}
                            onKeyPress={handleKeyPress}
                            placeholder={usageLimitExceeded ? "Token limit reached. Try again later." : "Type your message here..."}
                            rows="1"
                            disabled={!!serverError || usageLimitExceeded}
                        />
                        <input
                            type="file"
                            ref={fileInputRef}
                            onChange={handleFileSelect}
                            style={{display: 'none'}}
                            accept="image/jpeg,image/png,image/gif,application/pdf"
                            multiple
                        />
                        <button
                            type="button"
                            className="sealab-livechat__attachment-btn"
                            onClick={openFileSelector}
                            disabled={!!serverError || usageLimitExceeded}
                            aria-label="Add attachment"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor"
                                 viewBox="0 0 16 16" className="sealab-livechat__attachment-icon">
                                <path
                                    d="M4.5 3a2.5 2.5 0 0 1 5 0v9a1.5 1.5 0 0 1-3 0V5a.5.5 0 0 1 1 0v7a.5.5 0 0 0 1 0V3a1.5 1.5 0 1 0-3 0v9a2.5 2.5 0 0 0 5 0V5a.5.5 0 0 1 1 0v7a3.5 3.5 0 1 1-7 0V3z"/>
                            </svg>
                        </button>
                        <button
                            type="submit"
                            className={`sealab-livechat__send-btn ${(isLoading || (!inputValue.trim() && attachments.length === 0) || !!serverError || usageLimitExceeded) ? 'sealab-livechat__send-disabled' : ''}`}
                            disabled={isLoading || (!inputValue.trim() && attachments.length === 0) || !!serverError || usageLimitExceeded}
                            aria-label="Send message"
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor"
                                 viewBox="0 0 16 16" className="sealab-livechat__send-icon">
                                <path
                                    d="M15.964.686a.5.5 0 0 0-.65-.65L.767 5.855H.766l-.452.18a.5.5 0 0 0-.082.887l.41.26.001.002 4.995 3.178 3.178 4.995.002.002.26.41a.5.5 0 0 0 .886-.083l6-15Zm-1.833 1.89L6.637 10.07l-.215-.338a.5.5 0 0 0-.154-.154l-.338-.215 7.494-7.494 1.178-.471-.47 1.178Z"/>
                            </svg>
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default ChatBot;